@if (report; as data) {
  <div class="crr">
    <header class="crr-header">
      <div class="crr-header__text">
        <h2 class="crr-title">Cash Report</h2>
        <p class="crr-subtitle">
          {{ data.date_range_label }} · {{ data.employee_count }}
          {{ data.employee_count === 1 ? 'employee' : 'employees' }} with cash records
        </p>
      </div>
      <div class="crr-exports">
        <button
          type="button"
          class="crr-export-btn"
          [disabled]="exportingPdf || exportingExcel || !data.employees.length"
          (click)="exportPdf()"
        >
          {{ exportingPdf ? 'Exporting…' : 'Export PDF' }}
        </button>
        <button
          type="button"
          class="crr-export-btn"
          [disabled]="exportingPdf || exportingExcel || !data.employees.length"
          (click)="exportExcel()"
        >
          {{ exportingExcel ? 'Exporting…' : 'Export Excel' }}
        </button>
      </div>
    </header>

    @if (!data.employees.length) {
      <div class="crr-empty">No cash reconciliation entries for this range.</div>
    } @else {
      <div
        class="crr-tabs-wrap"
        [class.crr-tabs-wrap--fade-left]="canScrollLeft"
        [class.crr-tabs-wrap--fade-right]="canScrollRight"
      >
        <button
          type="button"
          class="crr-tabs-nav crr-tabs-nav--left"
          [class.crr-tabs-nav--visible]="canScrollLeft"
          [disabled]="!canScrollLeft"
          aria-label="Scroll employee tabs left"
          (click)="scrollTabs('left')"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M10 3.5L5.5 8L10 12.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <div
          #tabsScroll
          class="crr-tabs"
          role="tablist"
          aria-label="Employees"
          (scroll)="onTabsScroll()"
        >
          @for (emp of data.employees; track trackByEmployeeId($index, emp)) {
            <button
              type="button"
              role="tab"
              class="crr-tab"
              [class.crr-tab--active]="activeEmployeeId === emp.employee_id"
              [attr.aria-selected]="activeEmployeeId === emp.employee_id"
              (click)="selectEmployee(emp.employee_id)"
            >
              {{ emp.name }}
            </button>
          }
        </div>
        <button
          type="button"
          class="crr-tabs-nav crr-tabs-nav--right"
          [class.crr-tabs-nav--visible]="canScrollRight"
          [disabled]="!canScrollRight"
          aria-label="Scroll employee tabs right"
          (click)="scrollTabs('right')"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M6 3.5L10.5 8L6 12.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>

      @if (activeEmployee; as emp) {
        <div class="crr-kpi-row">
          <div class="crr-kpi">
            <span class="crr-kpi__label">
              Total cash collected
              <app-info-tip
                label="Total cash collected"
                [text]="tips.totalCashCollected"
              ></app-info-tip>
            </span>
            <span class="crr-kpi__value">{{
              formatKpiMoney(emp.totals.total_cash_collected)
            }}</span>
          </div>
          <div class="crr-kpi">
            <span class="crr-kpi__label">
              Expected closing
              <app-info-tip
                label="Expected closing"
                [text]="tips.totalExpectedClosing"
              ></app-info-tip>
            </span>
            <span class="crr-kpi__value">{{
              formatKpiMoney(emp.totals.total_expected_closing)
            }}</span>
          </div>
          <div class="crr-kpi">
            <span class="crr-kpi__label">
              Actual closing
              <app-info-tip label="Actual closing" [text]="tips.totalActualClosing"></app-info-tip>
            </span>
            <span class="crr-kpi__value">{{
              formatKpiMoney(emp.totals.total_actual_closing)
            }}</span>
          </div>
          <div class="crr-kpi">
            <span class="crr-kpi__label">
              Delta (over / short)
              <app-info-tip label="Delta (over / short)" [text]="tips.totalDelta"></app-info-tip>
            </span>
            <span class="crr-kpi__value">{{ formatKpiMoney(emp.totals.sum_of_deltas) }}</span>
          </div>
          <div class="crr-kpi">
            <span class="crr-kpi__label">
              Records Included
              <app-info-tip label="Records Included" [text]="tips.recordsIncluded"></app-info-tip>
            </span>
            <span class="crr-kpi__value">{{ emp.totals.records_included }}</span>
          </div>
        </div>

        @if (!emp.rows.length) {
          <div class="crr-empty">No cash entries for this employee in the selected range.</div>
        } @else {
          <div class="crr-table-wrap">
            <table class="crr-table">
              <thead>
                <tr>
                  <th>
                    Date
                    <app-info-tip label="Date" [text]="tips.date"></app-info-tip>
                  </th>
                  <th>
                    Location
                    <app-info-tip label="Location" [text]="tips.location"></app-info-tip>
                  </th>
                  <th class="crr-num">
                    Opening Balance
                    <app-info-tip
                      label="Opening Balance"
                      [text]="tips.openingBalance"
                    ></app-info-tip>
                  </th>
                  <th class="crr-num">
                    Extra Cash Added
                    <app-info-tip
                      label="Extra Cash Added"
                      [text]="tips.extraCashAdded"
                    ></app-info-tip>
                  </th>
                  <th class="crr-num">
                    Cash Collected
                    <app-info-tip label="Cash Collected" [text]="tips.cashCollected"></app-info-tip>
                  </th>
                  <th class="crr-num">
                    Expected Closing
                    <app-info-tip
                      label="Expected Closing"
                      [text]="tips.expectedClosing"
                    ></app-info-tip>
                  </th>
                  <th class="crr-num">
                    Actual Closing
                    <app-info-tip label="Actual Closing" [text]="tips.actualClosing"></app-info-tip>
                  </th>
                  <th class="crr-num">
                    Delta
                    <app-info-tip label="Delta" [text]="tips.delta"></app-info-tip>
                  </th>
                </tr>
              </thead>
              <tbody>
                @for (row of emp.rows; track trackByRowId($index, row)) {
                  <tr>
                    <td>{{ row.date || '—' }}</td>
                    <td>{{ row.location || '—' }}</td>
                    <td class="crr-num">{{ formatCellMoney(row.opening_balance) }}</td>
                    <td class="crr-num">{{ formatCellMoney(row.extra_cash_total) }}</td>
                    <td class="crr-num">{{ formatCellMoney(row.cash_collected) }}</td>
                    <td class="crr-num">{{ formatCellMoney(row.expected_closing) }}</td>
                    <td class="crr-num">{{ formatCellMoney(row.closing_balance) }}</td>
                    <td class="crr-num">{{ formatCellMoney(row.delta) }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      }
    }
  </div>
}
